import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import ImageUpload from "../components/ImageUpload";
import { FALLBACK_CATEGORIES, FALLBACK_CITIES } from "../constants/mcevents";
import { BadgeCheck, Copy, Image as ImageIcon, Loader2, Pencil, Plus, Save, Trash2, X } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function imgSrc(v) {
  if (!v) return null;
  if (v.startsWith("http") || v.startsWith("/")) return v;
  return `${BACKEND}/api/files/${v}`;
}

const catLabel = (id) => FALLBACK_CATEGORIES.find((c) => c.id === id)?.label || id;

export default function AdminArtists() {
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async (initial = false) => {
    if (initial) setLoading(true);
    try {
      const { data } = await api.get("/admin/artists");
      setArtists(data.artists || []);
    } catch (e) { toast.error(formatApiError(e)); }
    if (initial) setLoading(false);
  };
  useEffect(() => { load(true); }, []);

  const toggleVerify = async (a) => {
    setBusy(true);
    try {
      await api.patch(`/admin/artists/${a.id}`, { verified: !a.verified });
      toast.success(a.verified ? "Badge vérifié retiré" : "Artiste vérifié ✓");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="text-center font-mono text-sm uppercase tracking-wider text-muted-foreground py-12">Chargement…</div>;

  return (
    <div data-testid="admin-artists-page">
      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-6">
        <div className="card-mc p-5 bg-[hsl(var(--muted))]/40 flex-1">
          <p className="overline mb-2">Artistes · Gestion complète</p>
          <p className="text-sm">Ajoutez un artiste (compte + profil), modifiez n'importe quel profil, attribuez le badge « Vérifié ». Les photos/vidéos en attente sont dans l'onglet Médias.</p>
        </div>
        <button data-testid="admin-add-artist-btn" onClick={() => setAdding(true)} className="btn-primary whitespace-nowrap">
          <Plus className="w-4 h-4 mr-2" /> Ajouter un artiste
        </button>
      </div>

      {artists.length === 0 ? (
        <div className="card-mc p-12 text-center text-muted-foreground">Aucun profil artiste pour le moment.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {artists.map((a) => (
            <div key={a.id} className="card-mc overflow-hidden" data-testid={`admin-artist-${a.id}`}>
              <div className="aspect-[4/3] bg-muted relative">
                {imgSrc(a.cover_image) ? (
                  <img src={imgSrc(a.cover_image)} alt={a.stage_name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-muted-foreground"><ImageIcon className="w-8 h-8" /></div>
                )}
                {a.verified && (
                  <span className="absolute top-3 left-3 inline-flex items-center gap-1 bg-[hsl(var(--primary))] text-white text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-full" data-testid={`verified-badge-${a.id}`}>
                    <BadgeCheck className="w-3 h-3" /> Vérifié
                  </span>
                )}
                {a.pending_media_count > 0 && (
                  <span className="absolute top-3 right-3 bg-[hsl(var(--secondary))] text-white text-[10px] font-mono px-2 py-1 rounded-full" data-testid={`pending-media-badge-${a.id}`}>
                    {a.pending_media_count} média(s) en attente
                  </span>
                )}
              </div>
              <div className="p-4">
                <p className="font-display font-bold">{a.stage_name}</p>
                <p className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground mt-0.5">
                  {catLabel(a.category)} · {a.city}
                </p>
                <p className="text-[11px] text-muted-foreground mt-1">{a.user?.name} · {a.user?.email}</p>
                <div className="flex gap-2 mt-3">
                  <button data-testid={`edit-artist-${a.id}`} onClick={() => setEditing(a)} className="btn-outline text-xs flex-1">
                    <Pencil className="w-3.5 h-3.5 mr-1.5" /> Modifier
                  </button>
                  <button data-testid={`verify-artist-${a.id}`} disabled={busy} onClick={() => toggleVerify(a)}
                    className={`text-xs flex-1 ${a.verified ? "btn-ghost" : "btn-secondary"}`}>
                    <BadgeCheck className="w-3.5 h-3.5 mr-1.5" /> {a.verified ? "Retirer badge" : "Vérifier"}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {adding && <AddArtistModal onClose={() => setAdding(false)} onCreated={() => load()} />}
      {editing && <EditArtistModal artist={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

const inputCls = "w-full border border-border px-3 py-2 rounded-sm bg-card text-sm";
const Lbl = ({ children }) => <label className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">{children}</label>;

function AddArtistModal({ onClose, onCreated }) {
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null); // {access_code, user}
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    stage_name: "", category: "chanteur", city: "Conakry", bio: "",
    base_price_gnf: 0, profile_photo: "", cover_image: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/artists", { ...form, base_price_gnf: Number(form.base_price_gnf) || 0 });
      setCreated(data);
      onCreated();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(created.access_code);
    toast.success("Code copié");
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="add-artist-modal">
      {created ? (
        <div className="bg-card card-mc w-full max-w-md p-8 text-center">
          <BadgeCheck className="w-12 h-12 mx-auto text-[hsl(var(--accent))]" />
          <h3 className="font-display font-black text-2xl tracking-tighter mt-3">Artiste créé ✓</h3>
          <p className="text-sm text-muted-foreground mt-2">Transmettez ce code d'accès à <strong>{created.user.name}</strong> ({created.user.email}). Il ne sera plus jamais affiché.</p>
          <div className="mt-5 flex items-center justify-center gap-2">
            <span className="font-mono font-black text-3xl tracking-[0.3em] bg-muted px-4 py-2" data-testid="artist-access-code">{created.access_code}</span>
            <button data-testid="copy-access-code-btn" onClick={copyCode} className="btn-outline p-2.5"><Copy className="w-4 h-4" /></button>
          </div>
          <button data-testid="close-created-btn" onClick={onClose} className="btn-primary w-full mt-6">Terminé</button>
        </div>
      ) : (
        <form onSubmit={submit} className="bg-card card-mc w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="font-display font-black text-2xl tracking-tighter">Ajouter un artiste</h3>
              <p className="text-xs text-muted-foreground mt-1">Compte + profil créés par l'admin (KYC approuvé d'office). Le code d'accès sera affiché une seule fois.</p>
            </div>
            <button type="button" data-testid="close-add-artist-btn" onClick={onClose} className="btn-ghost p-2"><X className="w-4 h-4" /></button>
          </div>

          <p className="overline mb-2">Compte</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
            <div><Lbl>Nom complet *</Lbl><input data-testid="add-artist-name" required minLength={2} value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} /></div>
            <div><Lbl>Email *</Lbl><input data-testid="add-artist-email" required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} /></div>
            <div><Lbl>Téléphone *</Lbl><input data-testid="add-artist-phone" required minLength={6} value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} placeholder="+224 620 00 00 00" /></div>
          </div>

          <p className="overline mb-2">Profil public</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><Lbl>Nom de scène *</Lbl><input data-testid="add-artist-stagename" required minLength={2} value={form.stage_name} onChange={(e) => set("stage_name", e.target.value)} className={inputCls} /></div>
            <div>
              <Lbl>Catégorie *</Lbl>
              <select data-testid="add-artist-category" value={form.category} onChange={(e) => set("category", e.target.value)} className={inputCls}>
                {FALLBACK_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <Lbl>Ville *</Lbl>
              <select data-testid="add-artist-city" value={form.city} onChange={(e) => set("city", e.target.value)} className={inputCls}>
                {FALLBACK_CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div><Lbl>Tarif de base (GNF)</Lbl><input data-testid="add-artist-price" type="number" min={0} value={form.base_price_gnf} onChange={(e) => set("base_price_gnf", e.target.value)} className={inputCls} /></div>
            <div className="md:col-span-2"><Lbl>Biographie</Lbl><textarea data-testid="add-artist-bio" rows={3} value={form.bio} onChange={(e) => set("bio", e.target.value)} className={inputCls} /></div>
            <div><ImageUpload value={form.profile_photo} onChange={(v) => set("profile_photo", v)} label="Photo de profil" testid="add-artist-profile-photo" aspect="aspect-square" /></div>
            <div><ImageUpload value={form.cover_image} onChange={(v) => set("cover_image", v)} label="Image de couverture" testid="add-artist-cover" aspect="aspect-[4/3]" /></div>
          </div>

          <div className="flex gap-2 justify-end mt-6">
            <button type="button" onClick={onClose} className="btn-ghost text-sm">Annuler</button>
            <button type="submit" data-testid="submit-add-artist-btn" disabled={busy} className="btn-primary text-sm">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />} Créer l'artiste
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

function EditArtistModal({ artist, onClose, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    stage_name: artist.stage_name || "", category: artist.category || "chanteur",
    city: artist.city || "Conakry", country: artist.country || "Guinée",
    bio: artist.bio || "", years_experience: artist.years_experience || 0,
    base_price_gnf: artist.base_price_gnf || 0, price_max_gnf: artist.price_max_gnf || 0,
    profile_photo: artist.profile_photo || "", cover_image: artist.cover_image || "",
    photos: artist.photos || [], videos: artist.videos || [], music_links: artist.music_links || [],
    phone_public: artist.phone_public || "", whatsapp: artist.whatsapp || "",
    youtube: artist.youtube || "", tiktok: artist.tiktok || "", instagram: artist.instagram || "",
    facebook: artist.facebook || "", website: artist.website || "",
    travel_zone: artist.travel_zone || "", booking_conditions: artist.booking_conditions || "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const [newVideo, setNewVideo] = useState("");
  const [newMusic, setNewMusic] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch(`/admin/artists/${artist.id}`, {
        ...form,
        base_price_gnf: Number(form.base_price_gnf) || 0,
        price_max_gnf: Number(form.price_max_gnf) || 0,
        years_experience: Number(form.years_experience) || 0,
      });
      toast.success("Profil artiste mis à jour");
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="edit-artist-modal">
      <form onSubmit={submit} className="bg-card card-mc w-full max-w-3xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="font-display font-black text-2xl tracking-tighter">Modifier · {artist.stage_name}</h3>
            <p className="text-xs text-muted-foreground mt-1">Les photos/vidéos modifiées par l'admin sont approuvées d'office.</p>
          </div>
          <button type="button" data-testid="close-edit-artist-btn" onClick={onClose} className="btn-ghost p-2"><X className="w-4 h-4" /></button>
        </div>

        <p className="overline mb-2">Identité</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
          <div><Lbl>Nom de scène</Lbl><input data-testid="edit-artist-stagename" required minLength={2} value={form.stage_name} onChange={(e) => set("stage_name", e.target.value)} className={inputCls} /></div>
          <div>
            <Lbl>Catégorie</Lbl>
            <select data-testid="edit-artist-category" value={form.category} onChange={(e) => set("category", e.target.value)} className={inputCls}>
              {FALLBACK_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </div>
          <div><Lbl>Ville</Lbl><input data-testid="edit-artist-city" value={form.city} onChange={(e) => set("city", e.target.value)} className={inputCls} /></div>
          <div><Lbl>Pays</Lbl><input value={form.country} onChange={(e) => set("country", e.target.value)} className={inputCls} /></div>
          <div className="md:col-span-2"><Lbl>Biographie</Lbl><textarea data-testid="edit-artist-bio" rows={3} value={form.bio} onChange={(e) => set("bio", e.target.value)} className={inputCls} /></div>
          <div><Lbl>Années d'expérience</Lbl><input type="number" min={0} value={form.years_experience} onChange={(e) => set("years_experience", e.target.value)} className={inputCls} /></div>
        </div>

        <p className="overline mb-2">Tarifs & contact</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          <div><Lbl>Tarif min (GNF)</Lbl><input data-testid="edit-artist-price" type="number" min={0} value={form.base_price_gnf} onChange={(e) => set("base_price_gnf", e.target.value)} className={inputCls} /></div>
          <div><Lbl>Tarif max (GNF)</Lbl><input type="number" min={0} value={form.price_max_gnf} onChange={(e) => set("price_max_gnf", e.target.value)} className={inputCls} /></div>
          <div><Lbl>Téléphone</Lbl><input value={form.phone_public} onChange={(e) => set("phone_public", e.target.value)} className={inputCls} /></div>
          <div><Lbl>WhatsApp</Lbl><input value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} className={inputCls} /></div>
        </div>

        <p className="overline mb-2">Réseaux sociaux</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
          {["youtube", "tiktok", "instagram", "facebook", "website"].map((k) => (
            <div key={k}><Lbl>{k}</Lbl><input value={form[k]} onChange={(e) => set(k, e.target.value)} className={inputCls} placeholder="https://…" /></div>
          ))}
          <div><Lbl>Zone de déplacement</Lbl><input value={form.travel_zone} onChange={(e) => set("travel_zone", e.target.value)} className={inputCls} /></div>
        </div>

        <p className="overline mb-2">Images</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          <ImageUpload value={form.profile_photo} onChange={(v) => set("profile_photo", v)} label="Photo de profil" testid="edit-artist-profile-photo" aspect="aspect-square" />
          <ImageUpload value={form.cover_image} onChange={(v) => set("cover_image", v)} label="Couverture" testid="edit-artist-cover" aspect="aspect-[4/3]" />
        </div>
        <Lbl>Galerie ({form.photos.length}/8)</Lbl>
        <div className="grid grid-cols-4 gap-2 mt-1 mb-5">
          {form.photos.map((p, i) => (
            <div key={i} className="relative aspect-square bg-muted card-mc overflow-hidden group">
              <img src={imgSrc(p)} alt="" className="w-full h-full object-cover" />
              <button type="button" data-testid={`edit-remove-photo-${i}`} onClick={() => set("photos", form.photos.filter((_, j) => j !== i))}
                className="absolute top-1 right-1 bg-black/60 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-3 h-3" /></button>
            </div>
          ))}
          {form.photos.length < 8 && (
            <ImageUpload value="" onChange={(v) => v && set("photos", [...form.photos, v])} label="Ajouter" testid="edit-add-photo" aspect="aspect-square" />
          )}
        </div>

        <p className="overline mb-2">Vidéos & musique</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
          <div>
            <Lbl>Vidéos (YouTube)</Lbl>
            <div className="flex gap-2 mt-1">
              <input data-testid="edit-video-input" value={newVideo} onChange={(e) => setNewVideo(e.target.value)} className={inputCls} placeholder="https://youtube.com/…" />
              <button type="button" data-testid="edit-video-add" onClick={() => { if (newVideo.trim()) { set("videos", [...form.videos, newVideo.trim()]); setNewVideo(""); } }} className="btn-outline px-3"><Plus className="w-4 h-4" /></button>
            </div>
            {form.videos.map((u, i) => (
              <div key={i} className="flex items-center gap-2 text-xs mt-1.5 bg-muted px-2 py-1.5 rounded-sm">
                <span className="truncate flex-1">{u}</span>
                <button type="button" onClick={() => set("videos", form.videos.filter((_, j) => j !== i))}><Trash2 className="w-3.5 h-3.5 text-destructive" /></button>
              </div>
            ))}
          </div>
          <div>
            <Lbl>Extraits musicaux</Lbl>
            <div className="flex gap-2 mt-1">
              <input value={newMusic} onChange={(e) => setNewMusic(e.target.value)} className={inputCls} placeholder="YouTube / SoundCloud / Spotify" />
              <button type="button" onClick={() => { if (newMusic.trim()) { set("music_links", [...form.music_links, newMusic.trim()]); setNewMusic(""); } }} className="btn-outline px-3"><Plus className="w-4 h-4" /></button>
            </div>
            {form.music_links.map((u, i) => (
              <div key={i} className="flex items-center gap-2 text-xs mt-1.5 bg-muted px-2 py-1.5 rounded-sm">
                <span className="truncate flex-1">{u}</span>
                <button type="button" onClick={() => set("music_links", form.music_links.filter((_, j) => j !== i))}><Trash2 className="w-3.5 h-3.5 text-destructive" /></button>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-5">
          <Lbl>Conditions de réservation</Lbl>
          <textarea rows={2} value={form.booking_conditions} onChange={(e) => set("booking_conditions", e.target.value)} className={inputCls} />
        </div>

        <div className="flex gap-2 justify-end">
          <button type="button" onClick={onClose} className="btn-ghost text-sm">Annuler</button>
          <button type="submit" data-testid="submit-edit-artist-btn" disabled={busy} className="btn-primary text-sm">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />} Enregistrer
          </button>
        </div>
      </form>
    </div>
  );
}
